import { Quotation, QuotationOnlyFlights, RoomBaseConfig } from 'features/quotation/types';

export type QuotationImportStep =
	| 'quotation'
	| 'order'
	| 'passengers'
	| 'flights'
	| 'accommodations'
	| 'services'
	| 'summary';

export interface WizardSavedService {
	keyId: string;
	label: string;
	serviceType: string;
}

export type PassengerSlotKind = 'adult' | 'child' | 'infant';

export interface PassengerSlot {
	id: string;
	kind: PassengerSlotKind;
	childAge?: number;
	label: string;
}

export interface WizardSavedPassenger {
	keyId: string;
	passengerId?: string;
	name: string;
	lastName: string;
	slotId: string;
	/** Etiqueta del slot al guardar (ej. "Menor 1 (10 años)"). */
	slotLabel: string;
	slotKind: PassengerSlotKind;
}

export interface QuotationImportPersistedState {
	quotationId: number;
	saleOrderId: string;
	step: QuotationImportStep;
	savedPassengers: WizardSavedPassenger[];
	currentPassengerSlotIndex: number;
	savedServices?: WizardSavedService[];
}

export type LoadedQuotation = Quotation | QuotationOnlyFlights;

export const isFullQuotation = (q: LoadedQuotation): q is Quotation =>
	'destination' in q && Array.isArray(q.destination);

export const getRoomBaseConfigFromQuotation = (q: LoadedQuotation): RoomBaseConfig[] => {
	if ('roomBaseConfig' in q && q.roomBaseConfig?.length) {
		return q.roomBaseConfig;
	}
	if ('passengerConfig' in q && q.passengerConfig) {
		return [
			{
				adults: q.passengerConfig.adults,
				childs: q.passengerConfig.childrenAges,
				infants: q.passengerConfig.infantAges,
			},
		];
	}
	return [{ adults: 1, childs: [], infants: [] }];
};

export const buildPassengerSlots = (roomBaseConfig: RoomBaseConfig[]): PassengerSlot[] => {
	const slots: PassengerSlot[] = [];
	let adultIndex = 0;
	let childIndex = 0;
	let infantIndex = 0;

	roomBaseConfig.forEach((room, roomIndex) => {
		for (let i = 0; i < room.adults; i += 1) {
			adultIndex += 1;
			slots.push({
				id: `room-${roomIndex}-adult-${i}`,
				kind: 'adult',
				label: `Adulto ${adultIndex}${roomBaseConfig.length > 1 ? ` (hab. ${roomIndex + 1})` : ''}`,
			});
		}
		room.childs.forEach((age, i) => {
			childIndex += 1;
			slots.push({
				id: `room-${roomIndex}-child-${i}`,
				kind: 'child',
				childAge: age,
				label: `Menor ${childIndex} (${age} años)${roomBaseConfig.length > 1 ? ` — hab. ${roomIndex + 1}` : ''}`,
			});
		});
		room.infants.forEach((age, i) => {
			infantIndex += 1;
			slots.push({
				id: `room-${roomIndex}-infant-${i}`,
				kind: 'infant',
				childAge: age,
				label: `Infante ${infantIndex}${typeof age === 'number' ? ` (${age} años)` : ''}${
					roomBaseConfig.length > 1 ? ` — hab. ${roomIndex + 1}` : ''
				}`,
			});
		});
	});

	if (slots.length === 0) {
		slots.push({
			id: 'default-adult-1',
			kind: 'adult',
			label: 'Adulto 1',
		});
	}

	return slots;
};
